'use client'

import React, { useContext, useState, useCallback, useEffect, useRef, useId } from 'react'
import { SectionThemeContext } from '../../../contexts/SectionTheme.context'
import { Container } from '../../atoms/GridSystem'
import { Heading, Paragraph } from '../../atoms/Typography/Typography.component'
import Icon from '../../atoms/Icon/Icon.component'
import ResourceHubLandingView from './ResourceHubLandingView.component'
import ResourceHubFiltersBar from './ResourceHubFiltersBar.component'
import ResourceHubFiltersModal from './ResourceHubFiltersModal.component'
import ResourceHubResultsView from './ResourceHubResultsView.component'
import Image from 'next/image'
import { NormalizedResourceHubProps } from '../../../models/organisms/ResourceHub'
import { NormalizedImageData } from '../../../models/base/ImageBase'
import { ColorSchemeTypes } from '../../../types/ColorScheme'
import { ResourceCategory } from '../../../types/ResourceHub'
import useResourceHub from '../../../hooks/useResourceHub'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import * as S from './ResourceHub.styles'
import { useLocale } from 'next-intl'

type ViewState = 'landing' | 'results'

function isValidImage(img: NormalizedImageData | undefined): img is NormalizedImageData {
  return !!img?.src
}

function hasValidImagePair(imgs: NormalizedImageData[] | undefined): imgs is [NormalizedImageData, NormalizedImageData] {
  return Array.isArray(imgs) && imgs.length === 2 && imgs.every(isValidImage)
}

const ResourceHub: React.FC<NormalizedResourceHubProps> = ({
  sectionId,
  colorScheme,
  title,
  preTitle,
  pageSize = 12,
  topLeftImages,
  topRightImage,
  bottomLeftImage,
  bottomRightImages,
  noResultsButton,
}) => {
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const activeColorScheme = (colorScheme || selectedColorScheme) as ColorSchemeTypes
  const locale = useLocale()
  const titleHeadingId = useId()
  const filtersModalReturnFocusRef = useRef<HTMLElement | null>(null)

  const [searchQuery, setSearchQuery] = useState(() => {
    if (typeof window === 'undefined') return ''

    return new URLSearchParams(window.location.search).get('q') || ''
  })
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 400)

  const [viewState, setViewState] = useState<ViewState>(() => {
    if (typeof window === 'undefined') return 'landing'

    const params = new URLSearchParams(window.location.search)
    const hasParams = params.has('q') || params.has('category') || params.has('industry') || params.has('gas')
    return hasParams ? 'results' : 'landing'
  })

  const {
    items,
    isLoading,
    isFetching,
    isError,
    currentPage,
    totalPages,
    totalItems,
    hasNextPage,
    hasPrevPage,
    nextPage,
    prevPage,
    goToPage,
    filters,
    activeFilters,
    updateFilter,
    clearFilter,
    clearFilters,
    sortOrder,
    setSortOrder,
  } = useResourceHub({
    pageSize,
    locale,
    enabled: true,
    searchQuery: debouncedSearchQuery,
    syncSearchParams: true,
  })

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
    goToPage(1)
    setViewState('results')
  }, [])

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value)
    goToPage(1)
  }, [])

  const handleCategoryClick = useCallback(
    (category: ResourceCategory) => {
      updateFilter('resourceCategory', category)
      setViewState('results')
    },
    [updateFilter]
  )

  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)

  const handleOpenFiltersModal = useCallback(() => {
    filtersModalReturnFocusRef.current = document.activeElement as HTMLElement | null
    setIsFiltersModalOpen(true)
  }, [])

  const handleCloseFiltersModal = useCallback((showResults: boolean = false) => {
    setIsFiltersModalOpen(false)
    if (showResults){
      setViewState('results')
    }
  }, [])

  useEffect(() => {
    if(Object.keys(activeFilters).length > 0 && viewState === 'landing') {
      setViewState('results')
    }
  }, [activeFilters])

  return (
    <S.ResourceHub
      id={sectionId}
      colorScheme={activeColorScheme}
      data-view-state={viewState}
      aria-busy={isFetching || isLoading}
      aria-labelledby={title ? titleHeadingId : undefined}
    >
      <S.ResourceHubOverlay $visible={isFetching || isLoading}>
        <Icon name="spinner" aria-hidden />
      </S.ResourceHubOverlay>
      <Container>
        {
          viewState === 'landing' ? (
            <S.ResourceHubLandingWrapper>
              {hasValidImagePair(topLeftImages) ? (
                <S.TopLeftImages>
                  <S.LandingImageWrapper>
                    <Image src={topLeftImages[0].src!} alt={topLeftImages[0].alt || ''} fill />
                  </S.LandingImageWrapper>
                  <S.LandingImageWrapper>
                    <Image src={topLeftImages[1].src!} alt={topLeftImages[1].alt || ''} fill />
                  </S.LandingImageWrapper>
                </S.TopLeftImages>
              ) : null}

              {isValidImage(topRightImage) ? (
                <S.TopRightImage>
                  <S.LandingImageWrapper>
                    <Image src={topRightImage.src!} alt={topRightImage.alt || ''} fill />
                  </S.LandingImageWrapper>
                </S.TopRightImage>
              ) : null}

              {isValidImage(bottomLeftImage) ? (
                <S.BottomLeftImage>
                  <S.LandingImageWrapper>
                    <Image src={bottomLeftImage.src!} alt={bottomLeftImage.alt || ''} fill />
                  </S.LandingImageWrapper>
                </S.BottomLeftImage>
              ) : null}

              {hasValidImagePair(bottomRightImages) ? (
                <S.BottomRightImages>
                  <S.LandingImageWrapper>
                    <Image src={bottomRightImages[0].src!} alt={bottomRightImages[0].alt || ''} fill />
                  </S.LandingImageWrapper>
                  <S.LandingImageWrapper>
                    <Image src={bottomRightImages[1].src!} alt={bottomRightImages[1].alt || ''} fill />
                  </S.LandingImageWrapper>
                </S.BottomRightImages>
              ) : null}

              <S.ResourceHubHeader colorScheme={activeColorScheme}>
                {title ? (
                  <Heading as="h2" id={titleHeadingId} typo="headingMD" content={title} />
                ) : null}
                {preTitle ? (
                  <Paragraph as="div" typo="headingXXL" weight="bold" content={preTitle} />
                ) : null}
              </S.ResourceHubHeader>
        
              <ResourceHubLandingView
                filters={filters}
                onFilterSelect={handleCategoryClick}
                onSearch={handleSearch}
                onOpenFiltersModal={handleOpenFiltersModal}
                colorScheme={activeColorScheme}
              />
            </S.ResourceHubLandingWrapper>
          ) : (
            <S.ResourceHubResultsWrapper>
              <ResourceHubFiltersBar
                filters={filters}
                activeFilters={activeFilters}
                onFilterToggle={updateFilter}
                onFilterClear={clearFilter}
                onSearch={handleSearch}
                onSearchChange={handleSearchChange}
                searchQuery={searchQuery}
                onOpenFiltersModal={handleOpenFiltersModal}
                colorScheme={activeColorScheme}
                totalItems={totalItems}
                sortOrder={sortOrder}
                onSortChange={setSortOrder}
                onClearAllFilters={clearFilters}
              />
              <ResourceHubResultsView
                items={items}
                isLoading={isLoading}
                isError={isError}
                totalPages={totalPages}
                totalItems={totalItems}
                hasNextPage={hasNextPage}
                hasPrevPage={hasPrevPage}
                nextPage={nextPage}
                prevPage={prevPage}
                goToPage={goToPage}
                currentPage={currentPage}
                colorScheme={activeColorScheme}
                noResultsButton={noResultsButton}
              />
            </S.ResourceHubResultsWrapper>
          )
        }
      </Container>

      <ResourceHubFiltersModal
        isOpen={isFiltersModalOpen}
        filters={filters}
        activeFilters={activeFilters}
        onFilterToggle={updateFilter}
        onFilterClear={clearFilter}
        onClearAllFilters={clearFilters}
        sortOrder={sortOrder}
        onSortChange={setSortOrder}
        totalItems={totalItems}
        colorScheme={activeColorScheme}
        onClose={handleCloseFiltersModal}
        returnFocusRef={filtersModalReturnFocusRef}
      />
    </S.ResourceHub>
  )
}

export default ResourceHub
